import { useRef, useState } from "react";
import { collection, doc, getDoc, getDocs, writeBatch } from "firebase/firestore";
import useFirebaseConfig from "shared/hooks/useFirebaseConfig";
import { ReleaseEntry } from "../homeworkReleasePlan";
import { commitReleaseOperation, createReleaseOperation } from "../releaseWriter";
import { PendingRelease, readPendingRelease, savePendingRelease } from "../releaseRecovery";
import { validateReleaseContent } from "../releasePreflight";

/** Persist stable IDs before writing, then checkpoint each atomic batch. */
const useReleaseWriter = (scope: "ReportAssignLesson" | "ReportAssignStudent" | "ReportAssignBranch") => {
    const { dbFirestore, auth } = useFirebaseConfig();
    const key = auth.currentUser ? `tossenglish:pending-release:${auth.currentUser.uid}:${scope}` : undefined;
    const recoveryError = useRef<string>();
    const pending = useRef<PendingRelease>();
    const loadedKey = useRef<string>();
    if (key && loadedKey.current !== key) {
        loadedKey.current = key;
        try {
            pending.current = readPendingRelease(window.localStorage, key, scope);
            recoveryError.current = undefined;
        } catch {
            recoveryError.current = "이전 배포의 복구 기록을 읽지 못했습니다. 브라우저 저장소 설정을 확인해주세요.";
        }
    }
    const [, refresh] = useState(0);
    const inFlight = useRef(false);

    const execute = async (entries?: ReleaseEntry[]): Promise<boolean> => {
        if (inFlight.current) return false;
        inFlight.current = true;
        try {
            if (!key) throw new Error("로그인 상태를 확인한 뒤 다시 시도해주세요.");
            if (recoveryError.current) throw new Error(recoveryError.current);
            if (entries && pending.current && pending.current.fingerprint !== JSON.stringify(entries)) {
                throw new Error("이전 배포가 완료되지 않았습니다. '미완료 배포 재시도' 버튼으로 기존 배포를 먼저 완료해주세요.");
            }
            const contentEntries = pending.current?.operation.writes || entries;
            if (!contentEntries?.length) throw new Error("재시도할 배포가 없습니다.");
            if (!pending.current || pending.current.operation.completed < pending.current.operation.writes.length) {
                await validateReleaseContent(contentEntries, {
                    readReport: async path => {
                        const snapshot = await getDoc(doc(dbFirestore, path));
                        return snapshot.exists() ? snapshot.data() : undefined;
                    },
                    readContent: async path => {
                        const snapshot = await getDocs(collection(dbFirestore, path));
                        return Object.fromEntries(snapshot.docs.map(item => [item.id, item.data()]));
                    },
                });
            }
            if (!pending.current) {
                if (!entries?.length) throw new Error("재시도할 배포가 없습니다.");
                const fingerprint = JSON.stringify(entries);
                const operationId = doc(collection(dbFirestore, entries[0].table)).id;
                const created = { fingerprint, operation: createReleaseOperation(JSON.parse(fingerprint), operationId) };
                try {
                    savePendingRelease(window.localStorage, key, created);
                } catch {
                    throw new Error("배포 복구 기록을 저장하지 못해 배포를 시작하지 않았습니다. 브라우저 저장 공간을 확인하거나 배포 대상을 나누어주세요.");
                }
                pending.current = created;
                refresh(value => value + 1);
            }
            const captured = pending.current;
            const { operation } = captured;
            try {
                await commitReleaseOperation(operation, async writes => {
                    const batch = writeBatch(dbFirestore);
                    writes.forEach(write => batch.set(doc(dbFirestore, write.table, write.id), write.data));
                    await batch.commit();
                }, 400, () => {
                    savePendingRelease(window.localStorage, key, captured);
                    refresh(value => value + 1);
                });
                window.localStorage.removeItem(key);
            } catch (error) {
                console.error("과제 배포 배치 저장 실패:", error);
                throw new Error(`전체 ${operation.writes.length}건 중 ${operation.completed}건의 저장이 확인되었습니다. '미완료 배포 재시도'를 누르면 기존 선택과 일정으로 중복 없이 이어서 처리합니다.`);
            }
            pending.current = undefined;
            refresh(value => value + 1);
            return true;
        } finally {
            inFlight.current = false;
        }
    };

    return {
        publish: (entries: ReleaseEntry[]) => execute(entries),
        retryPending: () => execute(),
        pendingReleaseCount: pending.current?.operation.writes.length || 0,
    };
};

export default useReleaseWriter;
